import { memo, useState } from 'react';
import { IconChevron, toolIcon } from '../components/icons';
import { Badge, Button } from '../components/ui';
import { zh } from '../i18n';
import { cn } from '../lib/cn';
import { formatBytes, formatDurationSeconds } from '../lib/format';
import type { ToolItem } from '../stream/streamReducer';

/**
 * Collapsed 44 px row → `[圖示] 讀取檔案 · MEMORY.md ✓ 0.4s`; expanded adds the
 * mono input with copy, the result summary and the duration (SPEC §10).
 *
 * The read-only story: a tool error whose text says `EROFS`/`EACCES` under
 * `/shared/*` renders 「這個空間你只有唯讀權限」 above the raw message — the
 * kernel refused the write, and that is the one sentence that explains it.
 */

const RO_PATTERN = /\b(EROFS|EACCES)\b/;

function isReadOnlySpaceError(item: ToolItem): boolean {
  if (!item.isError) return false;
  const haystack = `${item.resultSummary ?? ''} ${item.errorHint ?? ''}`;
  return RO_PATTERN.test(haystack) && /\/shared\//.test(haystack);
}

function statusLabel(item: ToolItem): string {
  switch (item.status) {
    case 'running':
      return zh.tool.running;
    case 'denied':
      return zh.tool.denied;
    case 'interrupted':
      return zh.tool.interrupted;
    case 'error':
      return zh.tool.failed;
    case 'ok':
      return item.durationMs === null
        ? zh.tool.ok
        : zh.tool.duration(formatDurationSeconds(item.durationMs));
    default:
      return '';
  }
}

function prettyInput(input: unknown): string {
  if (input === undefined || input === null) return '';
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

function sameItems(a: readonly ToolItem[], b: readonly ToolItem[]): boolean {
  return a.length === b.length && a.every((item, index) => item === b[index]);
}

/**
 * The comparator matters: `buildRows` allocates a fresh group array on every
 * stream tick, so the default shallow compare would re-render every settled
 * card ~16 times a second during a long turn. The ToolItem objects themselves
 * are only replaced when that specific call changes.
 */
export const ToolCard = memo(
  function ToolCard({ items }: { items: readonly ToolItem[] }): JSX.Element | null {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const head = items[0];
    if (!head) return null;

    const Icon = toolIcon(head.name);
    const grouped = items.length > 1;
    const anyError = items.some((item) => item.isError);
    const title = grouped ? zh.tool.group(head.label, items.length) : head.label;

    const copyInput = (text: string): void => {
      void navigator.clipboard
        ?.writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => undefined);
    };

    return (
      <div className={cn('tool', anyError && 'tool--error')}>
        <button
          type="button"
          className="tool__row"
          aria-expanded={expanded}
          aria-label={expanded ? zh.tool.collapse : zh.tool.expand}
          onClick={() => setExpanded((value) => !value)}
        >
          <Icon className="tool__icon" />
          <span className="tool__name">{title || head.name}</span>
          <span className="tool__summary">{head.inputSummary ?? ''}</span>
          <span className="tool__status">{statusLabel(head)}</span>
          {head.autoAllowed ? (
            <Badge tone="muted">
              {head.autoAllowed === 'grant' ? zh.tool.autoAllowedGrant : zh.tool.autoAllowedMode}
            </Badge>
          ) : null}
          <IconChevron
            size={16}
            className="tool__icon"
            style={{ transform: expanded ? 'rotate(90deg)' : undefined }}
          />
        </button>

        {expanded ? (
          <div className="tool__body">
            {items.map((item) => {
              const input = prettyInput(item.input);
              return (
                <div key={item.id} className="stack stack--sm">
                  {grouped ? <div className="tiny faint">{item.inputSummary ?? ''}</div> : null}
                  {input ? (
                    <>
                      <div className="row row--between">
                        <span className="tiny faint">{zh.tool.input}</span>
                        <Button variant="ghost" small onClick={() => copyInput(input)}>
                          {copied ? zh.app.copied : zh.tool.copyInput}
                        </Button>
                      </div>
                      <pre className="tool__pre">{input}</pre>
                    </>
                  ) : null}
                  {isReadOnlySpaceError(item) ? (
                    <div className="banner banner--warn" role="status">
                      <span className="banner__text">{zh.tool.readOnlySpace}</span>
                    </div>
                  ) : null}
                  <span className="tiny faint">{zh.tool.result}</span>
                  <pre className="tool__pre">{item.resultSummary ?? zh.tool.noResult}</pre>
                  <div className="tiny faint">
                    {item.resultBytes !== null
                      ? zh.tool.resultBytes(formatBytes(item.resultBytes))
                      : ''}
                    {item.durationMs !== null
                      ? ` · ${zh.tool.duration(formatDurationSeconds(item.durationMs))}`
                      : ''}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  },
  (prev, next) => sameItems(prev.items, next.items),
);
